import { useState, useMemo } from 'react';
import * as RadixSelect from '@radix-ui/react-select';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@utils/Cn.js';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
  error?: string | undefined;
  searchable?: boolean;
}

export default function Select({
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  disabled,
  className,
  label,
  required,
  error,
  searchable,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const isSearchable = searchable ?? options.length >= 7;

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchQuery.trim()) return options;
    const q = searchQuery.toLowerCase().trim();
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, searchQuery, isSearchable]);

  return (
    <div className="flex flex-col gap-1.5 w-full min-w-0">
      {label && (
        <label className="text-xs font-semibold text-ink-soft truncate">
          {label}
          {required && <span className="ml-1 text-red-edge">*</span>}
        </label>
      )}
      <RadixSelect.Root
        value={value}
        onValueChange={onValueChange}
        disabled={disabled ?? false}
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setSearchQuery('');
          }
        }}
      >
        <RadixSelect.Trigger
          className={cn(
            'flex h-11 w-full items-center justify-between gap-2 rounded-xl border bg-surface px-3 text-sm text-ink outline-none overflow-hidden min-w-0',
            'transition-all hover:border-line-strong',
            'focus:border-slate focus:shadow-[0_0_0_3px_rgba(15,118,110,0.15)]',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'data-[placeholder]:text-ink-faint',
            error ? 'border-red-edge' : 'border-line-strong',
            className,
          )}
        >
          <span className="truncate flex-1 min-w-0 text-left">
            <RadixSelect.Value placeholder={placeholder} />
          </span>
          <RadixSelect.Icon className="shrink-0">
            <ChevronDown
              size={15}
              className={cn('text-ink-faint transition-transform duration-200', open && 'rotate-180')}
            />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>

        <RadixSelect.Portal>
          <RadixSelect.Content
            className={cn(
              'z-[9999] min-w-[var(--radix-select-trigger-width)] w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]',
              'max-h-[var(--radix-select-content-available-height)] overflow-hidden',
              'rounded-2xl border border-line bg-surface shadow-[0_16px_48px_rgba(10,20,36,0.22)]',
              'animate-in fade-in-0 zoom-in-95 duration-150',
            )}
            position="popper"
            sideOffset={6}
            collisionPadding={12}
          >
            {isSearchable && (
              <div
                className="p-2 border-b border-line/60 bg-surface sticky top-0 z-20"
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="flex h-9 items-center gap-2 px-2.5 rounded-xl border border-line-strong bg-paper text-xs transition-all focus-within:border-slate">
                  <Search size={13} className="text-ink-faint shrink-0" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-transparent border-none outline-none w-full text-xs text-ink placeholder:text-ink-faint"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="text-ink-faint hover:text-ink transition shrink-0"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}

            <RadixSelect.Viewport className="p-1.5 max-h-56 sm:max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-rounded">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => (
                  <RadixSelect.Item
                    key={opt.value}
                    value={opt.value}
                    className={cn(
                      'relative flex cursor-pointer select-none items-center rounded-xl px-3 py-2.5 pr-8',
                      'text-sm text-ink outline-none truncate',
                      'data-[highlighted]:bg-paper data-[highlighted]:text-ink',
                      'data-[state=checked]:font-semibold data-[state=checked]:text-slate data-[state=checked]:bg-slate/10',
                      'transition-colors',
                    )}
                  >
                    <RadixSelect.ItemText className="truncate">{opt.label}</RadixSelect.ItemText>
                    <RadixSelect.ItemIndicator className="absolute right-2.5">
                      <Check size={14} className="text-slate" />
                    </RadixSelect.ItemIndicator>
                  </RadixSelect.Item>
                ))
              ) : (
                <div className="px-3 py-4 text-center text-xs font-medium text-ink-faint">
                  No matching options found
                </div>
              )}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
      {error && <p className="text-[11px] font-semibold text-red-fg">{error}</p>}
    </div>
  );
}
